import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpResponseHeaders,
  NamingStrategy
} from '@/data/protocols/http'
import { i18nTranslationHelper } from '@/i18n'
import {
  getRecaptchaToken,
  setRecaptchaToken
} from '@/presentation/contexts/recaptcha/recaptcha-token-store'
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'

axios.interceptors.response.use((response: AxiosResponse) => {
  if (response.data && response.headers['content-type'] === 'application/json') {
    response.data = camelizeKeys(response.data)
  }
  return response
})

axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config }

  const language = i18nTranslationHelper.getCurrentLanguage()
  if (language) {
    newConfig.headers['Accept-Language'] = language
  }

  const recaptchaToken = getRecaptchaToken()
  if (recaptchaToken) {
    newConfig.headers['X-Recaptcha-Token'] = recaptchaToken
    setRecaptchaToken(null)
  }

  if (!config.headers['Content-Type'] || config.headers['Content-Type'] === 'application/json') {
    if (config.params) {
      newConfig.params =
        newConfig.paramsNamingStrategy === NamingStrategy.snakeCase
          ? decamelizeKeys(newConfig.params)
          : camelizeKeys(newConfig.params)
    }
    if (config.data) {
      newConfig.data = decamelizeKeys(config.data)
    }
  }

  return newConfig
})

export class AxiosHttpClient implements HttpClient {
  async request(data: HttpRequest): Promise<HttpResponse> {
    let axiosResponse: AxiosResponse

    try {
      axiosResponse = await axios.request({
        url: data.url,
        method: data.method,
        data: data.body,
        headers: data.headers,
        paramsNamingStrategy: data.paramsNamingStrategy,
        params: data.params,
        withCredentials: data.withCredentials,
        onUploadProgress:
          data.onUploadProgress &&
          (progressEvent => data.onUploadProgress!(((progressEvent.progress || 0) * 100) | 0)),
        signal: data.signal ? data.signal : undefined
      })
    } catch (error) {
      axiosResponse = (error as AxiosError).response!
    }

    return {
      statusCode: axiosResponse?.status,
      body: axiosResponse?.data,
      headers: axiosResponse?.headers as HttpResponseHeaders
    }
  }
}
